import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiSend } from '../lib/api'
import { formatBitsPerSecond, formatBytes, formatDateTime } from '../lib/format'
import type { Agent, BandwidthTest } from '../lib/types'
import { EmptyState, ErrorBox, Loading } from './Feedback'

/** Ergebnis einer Richtung als Text: Rate und Menge, oder ein Strich. */
export function richtungText(bps?: number, bytes?: number): string {
  if (!bps) return '–'
  return bytes ? `${formatBitsPerSecond(bps)} (${formatBytes(bytes)})` : formatBitsPerSecond(bps)
}

const STATUS: Record<BandwidthTest['status'], string> = {
  running: 'läuft',
  done: 'fertig',
  failed: 'fehlgeschlagen',
}

/**
 * Bandbreitentest (Stufe 6.6): der Agent misst Download und Upload gegen die
 * Zentrale, je Richtung höchstens zehn Sekunden. Das lastet die Leitung des
 * Kunden in dieser Zeit aus; deshalb nur auf Knopfdruck und mit Pause.
 */
export function Bandbreitentest({ agents, editable }: { agents: Agent[]; editable: boolean }) {
  const queryClient = useQueryClient()
  const aktive = agents.filter((a) => a.status !== 'revoked')
  const [agentId, setAgentId] = useState(aktive[0]?.id ?? '')
  useEffect(() => {
    if (!aktive.some((a) => a.id === agentId) && aktive[0]) setAgentId(aktive[0].id)
  }, [aktive, agentId])

  const tests = useQuery({
    queryKey: ['bandbreite', agentId],
    queryFn: ({ signal }) => apiGet<BandwidthTest[]>(`/agents/${agentId}/bandbreite`, signal),
    enabled: agentId !== '',
    // Solange ein Test läuft, alle zwei Sekunden nachsehen.
    refetchInterval: (q) => (q.state.data?.some((t) => t.status === 'running') ? 2000 : false),
  })
  const starten = useMutation({
    mutationFn: () => apiSend<BandwidthTest>('POST', `/agents/${agentId}/bandbreite`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['bandbreite', agentId] }),
  })

  if (aktive.length === 0) return null
  const agent = aktive.find((a) => a.id === agentId)
  const laeuft = tests.data?.some((t) => t.status === 'running') ?? false

  return (
    <section className="card">
      <div className="card-head">
        <h2 className="card-title">Bandbreitentest</h2>
        <div className="row" style={{ gap: 8 }}>
          <label className="sr-only" htmlFor="bandbreite-agent">
            Agent
          </label>
          <select
            id="bandbreite-agent"
            className="input"
            style={{ height: 28, fontSize: 13 }}
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
          >
            {aktive.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} · {a.site_name}
              </option>
            ))}
          </select>
          {editable ? (
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={starten.isPending || laeuft || !agent?.connected}
              title={agent?.connected ? undefined : 'Der Agent ist nicht verbunden.'}
              onClick={() => starten.mutate()}
            >
              {laeuft ? 'Messung läuft …' : 'Bandbreite messen'}
            </button>
          ) : null}
        </div>
      </div>
      <div className="card-body stack" style={{ gap: 10 }}>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Misst die Leitung vom Standort zur Zentrale: je zehn Sekunden Download und Upload über eine eigene
          verschlüsselte Verbindung des Agents. Die Leitung ist in dieser Zeit ausgelastet; zwischen zwei Messungen
          liegen mindestens fünf Minuten.
        </p>
        {starten.isError ? (
          <p className="badge badge-crit" style={{ height: 'auto', padding: '8px 10px' }}>
            {(starten.error as Error).message}
          </p>
        ) : null}
        {tests.isError ? (
          <ErrorBox error={tests.error} what="Die Messungen" />
        ) : tests.isPending ? (
          <Loading what="Messungen" />
        ) : tests.data.length === 0 ? (
          <EmptyState title="Noch keine Messung" hint="Mit „Bandbreite messen“ die erste Messung starten." />
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Zeitpunkt</th>
                <th>Download</th>
                <th>Upload</th>
                <th>Stand</th>
                <th>Ausgelöst von</th>
              </tr>
            </thead>
            <tbody>
              {tests.data.map((t) => (
                <tr key={t.id}>
                  <td className="mono">{formatDateTime(t.started_at)}</td>
                  <td className="mono">{richtungText(t.down_bps, t.down_bytes)}</td>
                  <td className="mono">{richtungText(t.up_bps, t.up_bytes)}</td>
                  <td>
                    <span style={{ color: t.status === 'failed' ? 'var(--crit)' : undefined }}>{STATUS[t.status]}</span>
                    {t.error ? (
                      <div className="muted" style={{ fontSize: 12 }}>
                        {t.error}
                      </div>
                    ) : null}
                  </td>
                  <td className="muted">{t.requested_by || '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  )
}
